import { Card, Col, Divider, Flex, Form, Switch, Tooltip, Typography } from "antd";
import {
  CurrencyOperationCodesField,
  currencyOperationCodesRequiredRule,
  currencyOperationCodesToApiString,
} from "components";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CustomButton, BooleanRadioGroup, NumberInput, OtpInput, OtpTimeLeft, RadioButtonGroup } from "ui";
import {
  BID_REQUEST_TYPE_ENUM,
  ContractEntity,
  CreateBidType,
  DocUploadedResponse,
  NOTIFICATION_TYPE_ENUM,
  OPERATION_TYPE,
  QUOTE_SELECTION_ENUM,
  REQUEST_CHECK_STATUS_ENUM,
  RISK_LEVEL_ENUM,
  WalletRiskLevel,
} from "interfaces";
import { createBid, generateOperation } from "services";
import { useStores } from "stores";
import { useNotificationContext } from "providers";
import {
  _greaterThan,
  _isNumericWithPrecision,
  _isRequired,
  _lessOrEqual,
  parseErrorMessage,
  useWalletRisk,
} from "utils";
import { DIRECT_PAYMENT_CUSTOMER_LP, SMS_CODE_RETRY_DELAY_IN_SECONDS } from "utils/envConstants";
import { CreateBidForm } from "components";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { routes } from "constants/routes";
import { VerificationStep } from "./CheckBidInfo";
import { BankAccountSelection } from "./BankAccount";
import { FileUploadSection } from "./FileUpload";
import styles from "./modal.module.css";
import { CHANGE_WALLET_ADDRESS_MESSAGE } from "constants/network";
import { QuestionCircleOutlined } from "@ant-design/icons";

const CANCEL_BUTTON_TEXT = "Вернуться к редактированию";

export enum SEND_STEP {
  SEND = "SEND",
  CHECKING_WALLET = "CHECKING_WALLET",
  SMS = "SMS",
}

interface IProps {
  data: ContractEntity;
  contractLimitAmount?: number;
}

export const SendBidModal = observer((props: IProps) => {
  const { actionModals, contractCard, bankAccountsStore, user, bidCard } = useStores();
  const { isCustomer } = user.roles;
  const { openNotification } = useNotificationContext();
  const navigate = useNavigate();
  const { fiatCurrency, id: contractId } = props.data;

  const [loading, setLoading] = useState(false);
  const [sendStep, setSendStep] = useState(SEND_STEP.SEND);

  const [uploadedFiles, setUploadedFiles] = useState<DocUploadedResponse[]>([]);

  const [bidFrame, setBidFrame] = useState<boolean>(true);
  const [isCollectingQuotesImmediately, setIsCollectingQuotesImmediately] = useState(true);

  const [form] = Form.useForm();

  const [isWalletChecked, setIsWalletChecked] = useState(false);
  const [canCheckWallet, setCanCheckWallet] = useState(false);
  const [checkedWallet, setCheckedWallet] = useState<string | null>(null);
  const [walletAmlIdList, setWalletAmlIdList] = useState<string[]>([]);

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [savedRiskLevel, setSavedRiskLevel] = useState<WalletRiskLevel>();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [shouldStartOtpCountdown, setShouldStartOtpCountdown] = useState(false);

  const [storedFormValues, setStoredFormValues] = useState<Partial<CreateBidType>>({});

  const mergeStoredFormWithCurrencyCodes = useCallback((raw: Record<string, unknown>) => {
    const codes = (raw.currencyOperationCodes as string[] | undefined) ?? [];
    return {
      ...(raw as Partial<CreateBidType>),
      currencyOperationCode: currencyOperationCodesToApiString(codes),
    };
  }, []);

  const { riskLevel, amlId, riskStatus } = useWalletRisk(
    {
      walletAddress: form.getFieldsValue().destinationWallet || "",
      userId: user.userInfo?.id,
    },
    canCheckWallet
  );

  useEffect(() => {
    if (amlId && !walletAmlIdList.some((item) => amlId === item)) {
      setWalletAmlIdList([...walletAmlIdList, amlId]);
    }
  }, [amlId, walletAmlIdList]);

  useEffect(() => {
    if (riskLevel && riskStatus) {
      setSavedRiskLevel(riskLevel);
      setIsWalletChecked(true);
      setCheckedWallet(form.getFieldsValue().destinationWallet || "");
      setCanCheckWallet(false);
      setLoading(false);
      if (riskStatus === REQUEST_CHECK_STATUS_ENUM.SUCCESS) {
        const currentFormValues = form.getFieldsValue();
        setStoredFormValues(mergeStoredFormWithCurrencyCodes(currentFormValues as Record<string, unknown>));
        setSendStep(SEND_STEP.CHECKING_WALLET);
      }
      if (riskStatus === REQUEST_CHECK_STATUS_ENUM.APPROVE_NEEDED) {
        if (![RISK_LEVEL_ENUM.MEDIUM, RISK_LEVEL_ENUM.TIMEOUT].includes(riskLevel)) {
          setButtonDisabled(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskLevel, riskStatus]);

  const companyId = user.userInfo?.company_id;
  const isExpContract = contractCard.card.direction === "EXP";
  const isImpContract = contractCard.card.direction === "IMP";

  const isSendStep = sendStep === SEND_STEP.SEND;
  const isSmsStep = sendStep === SEND_STEP.SMS;
  const isCheckStep = sendStep === SEND_STEP.CHECKING_WALLET;
  const isVerificationStep = isCheckStep || isSmsStep;

  const startOtpCountdown = () => {
    setSendStep(SEND_STEP.SMS);
    setShouldStartOtpCountdown(true);
    form.setFieldsValue({
      otp: "",
    });
  };

  const sendSms = async () => {
    setLoading(true);

    await generateOperation(OPERATION_TYPE.REQUEST_CREATE)
      .then(() => {
        startOtpCountdown();
      })
      .catch((e) => {
        if (e.type === "/otp-error") {
          startOtpCountdown();
          openNotification({
            description: e.title || `Код можно отправить раз в ${SMS_CODE_RETRY_DELAY_IN_SECONDS} секунд`,
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        } else {
          openNotification({
            description: parseErrorMessage(e),
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        }
      })
      .finally(() => setLoading(false));
  };

  const sendBid = async () => {
    setLoading(true);
    const { currencyOperationCodes: _omitCodes, ...storedForApi } = storedFormValues as Partial<CreateBidType> & {
      currencyOperationCodes?: string[];
    };
    const formData: CreateBidType = {
      ...storedForApi,
      fiatAmount: Number(storedFormValues.fiatAmount),
      amount: storedFormValues.amount ?? undefined,
      contractId,
      invoices: uploadedFiles.length > 0 ? uploadedFiles.map((item) => item.id) : undefined,
      type: bidFrame ? BID_REQUEST_TYPE_ENUM.REQUEST_WITHOUT_AGENT : BID_REQUEST_TYPE_ENUM.REQUEST_WITH_AGENT,
      walletAmlIdList,
      collectQuotesImmediately: isCollectingQuotesImmediately,
      otp: form.getFieldValue("otp") || "",
      fiatCurrency: bidCard.bid.fiatCurrency,
      currencyOperationCode: storedFormValues.currencyOperationCode || "",
    };

    await createBid(formData)
      .then((bidId) => {
        const getBid = async () => {
          await bidCard
            .fetchCardById(bidId)
            .then(() => {
              if (bidCard.bid.id) {
                openNotification({
                  message: "Заявка создана успешно",
                  type: NOTIFICATION_TYPE_ENUM.SUCCESS,
                });
                handleCancel();
                actionModals.closeLastModal();
                setLoading(false);

                navigate(`${routes.bids}/${bidId}`);
              }
            })
            .catch((error) => {
              if (error.status === 404) {
                setTimeout(() => getBid(), 200);
              }
            });
        };
        if (bidId) {
          setTimeout(() => getBid(), 500);
        }
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        setLoading(false);
      });
  };

  const handleCancel = () => {
    if (sendStep === SEND_STEP.CHECKING_WALLET) {
      setSendStep(SEND_STEP.SEND);
    } else if (isSmsStep) {
      setSendStep(SEND_STEP.SEND);
      setShouldStartOtpCountdown(false);
    } else {
      setShouldStartOtpCountdown(false);
      actionModals.closeLastModal();
    }
    setStoredFormValues({});
  };

  const getSubmitButtonText = (loading: boolean): string => {
    if (loading) {
      return "Проверка кошелька";
    }

    switch (sendStep) {
      case SEND_STEP.SEND:
        return "Создать заявку";
      case SEND_STEP.CHECKING_WALLET:
        return "Всё верно";
      default:
        return "";
    }
  };

  const getIdForSubmitButton = () => {
    switch (sendStep) {
      case SEND_STEP.SEND:
        return "create-bid-next-button";
      default:
        return undefined;
    }
  };

  const getSubmitButtonTooltip = () => {
    if (buttonDisabled) {
      return CHANGE_WALLET_ADDRESS_MESSAGE;
    }

    return undefined;
  };

  const allowCustomCurrencyCodes = !user.roles.isCustomer;

  return (
    <>
      <Flex vertical>
        <Form
          form={form}
          name="sendBid"
          initialValues={{
            fiatAmount: "",
            otp: "",
            currencyOperationCodes: [],
          }}
          onFinish={(values) => {
            if (isSendStep) {
              // window.updateOnboardingElements?.();

              const currentFormValues = form.getFieldsValue();
              setStoredFormValues(mergeStoredFormWithCurrencyCodes(currentFormValues as Record<string, unknown>));

              if (bidFrame && contractCard.card.direction === "IMP") {
                const walletChecked = isWalletChecked && checkedWallet === form.getFieldsValue().destinationWallet;

                if (walletChecked) {
                  setSendStep(SEND_STEP.CHECKING_WALLET);
                } else {
                  setLoading(true);

                  setIsWalletChecked(false);
                  setCanCheckWallet(true);
                }
              } else {
                sendSms();
              }
            }
            if (isCheckStep) {
              sendSms();
            }
            if (isSmsStep) {
              sendBid();
            }
          }}
          className={styles.sendBidForm}
        >
          {isSendStep && (
            <Flex vertical>
              <Typography.Title level={1}>Заявка на проведение платежа</Typography.Title>
              <Divider className="margin-bottom-md margin-top-none" />
              <Flex vertical>
                <Flex vertical>
                  <Flex gap="large">
                    <Col id="create-bid-amount" style={{ flex: 2 }}>
                      <Form.Item
                        name="fiatAmount"
                        rules={[
                          _isRequired(`Введите сумму выплаты ${fiatCurrency ? `в ${fiatCurrency})` : ""}`),
                          ...(props.contractLimitAmount ? [_lessOrEqual(props.contractLimitAmount)] : []),
                          _greaterThan(0),
                          _isNumericWithPrecision(12),
                        ]}
                        validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      >
                        <NumberInput
                          placeholder={`Сумма ${isCustomer ? "" : "выплаты"} (${fiatCurrency})`}
                          disabled={loading}
                        />
                      </Form.Item>
                    </Col>
                  </Flex>
                  <Tooltip title="УВЭД выбирает код из списка по направлению контракта; оператор может добавить свой код">
                    <Col id="currencyOperationCode" style={{ flex: 1 }}>
                      <Form.Item
                        name="currencyOperationCodes"
                        rules={[currencyOperationCodesRequiredRule()]}
                        validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      >
                        <CurrencyOperationCodesField
                          allowCustomCodes={allowCustomCurrencyCodes}
                          contractDirection={contractCard.card.direction === "IMP" ? "IMP" : "EXP"}
                          disabled={loading}
                          placeholder="Код вида операции"
                        />
                      </Form.Item>
                    </Col>
                  </Tooltip>
                  <Flex
                    vertical
                    id={
                      isExpContract ? "create-exp-bid-without-agent-checkbox" : "create-imp-bid-without-agent-checkbox"
                    }
                  >
                    <BooleanRadioGroup
                      value={bidFrame}
                      onChange={(value) => {
                        setBidFrame(value);
                      }}
                      title={
                        <Typography.Title level={2} className="margin-bottom-none">
                          Контрагент сможет {contractCard.card.direction === "EXP" ? "отправить" : "принять"} платеж в
                          криптовалюте?
                          <Tooltip title="При активации данного параметра привлечение платежного агента не требуется">
                            <QuestionCircleOutlined style={{ width: "12px", height: "12px", marginLeft: "3px" }} />
                          </Tooltip>
                        </Typography.Title>
                      }
                      className="margin-bottom-md"
                    />
                    <Divider className="margin-bottom-md margin-top-none" />
                  </Flex>
                  <Form.Item name="collectQuotesImmediately">
                    <div>
                      <Typography.Title level={2} className="margin-bottom-xs">
                        Получать котировки сразу после согласования заявки
                        <Tooltip
                          title={
                            isCollectingQuotesImmediately
                              ? "Сбор котировок начнется сразу после согласования заявки оператором"
                              : "Запросить котировки можно будет позже в течение 90 дней"
                          }
                        >
                          <QuestionCircleOutlined style={{ width: "12px", height: "12px", marginLeft: "3px" }} />
                        </Tooltip>
                      </Typography.Title>
                    </div>
                    <div>
                      <Switch value={isCollectingQuotesImmediately} onChange={setIsCollectingQuotesImmediately} />
                    </div>
                    <Divider className="margin-bottom-md margin-top-md" />
                  </Form.Item>
                </Flex>

                {bidFrame && (
                  <CreateBidForm
                    title="Параметры цифровой валюты"
                    disabled={loading}
                    addressInputVisible={contractCard.card.direction === "IMP"}
                    onWalletChange={() => {
                      if (buttonDisabled) {
                        setButtonDisabled(false);
                        setIsWalletChecked(false);
                      }
                    }}
                    showRiskAlert={isWalletChecked && !loading}
                    riskLevel={savedRiskLevel}
                  />
                )}

                {isExpContract && DIRECT_PAYMENT_CUSTOMER_LP && (
                  <BankAccountSelection
                    loading={loading}
                    selectedAccountId={selectedAccountId}
                    bankAccountsStore={bankAccountsStore}
                    contractCard={contractCard}
                    onAccountChange={setSelectedAccountId}
                    form={form}
                  />
                )}

                <FileUploadSection
                  loading={loading}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  sendStep={sendStep}
                  setSendStep={setSendStep}
                />
              </Flex>
            </Flex>
          )}
          {isVerificationStep && (
            <VerificationStep
              destinationWallet={storedFormValues.destinationWallet || ""}
              fiatAmount={storedFormValues.fiatAmount || ""}
              digitalAmount={storedFormValues.amount || ""}
              network={storedFormValues.network || ""}
              bidFrame={bidFrame}
              isCustomer={isCustomer}
              currency={storedFormValues.currency || ""}
              fiatCurrency={fiatCurrency}
              currencyOperationCode={storedFormValues.currencyOperationCode || ""}
            />
          )}
          {isSmsStep && (
            <Flex vertical gap="small">
              <Card className="card-not-shadow border-tertiary">
                <Typography.Title level={2} className="text-center margin-bottom-lg">
                  Введите код из СМС для подтверждения
                </Typography.Title>

                <OtpInput disabled={loading} className="margin-none" onSendSms={sendSms} loading={loading} />
                <Form.Item className="margin-top-md">
                  <CustomButton
                    htmlType="submit"
                    disabled={loading || buttonDisabled}
                    loading={loading}
                    id={getIdForSubmitButton()}
                    tooltip={getSubmitButtonTooltip()}
                    fullWidth
                  >
                    Продолжить
                  </CustomButton>
                </Form.Item>

                <OtpTimeLeft onResend={sendSms} loading={loading} shouldStartCountdown={shouldStartOtpCountdown} />
              </Card>
              <Divider />
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="default"
                onClick={handleCancel}
                fullWidth
              >
                {CANCEL_BUTTON_TEXT}
              </CustomButton>
            </Flex>
          )}

          {!isSmsStep && (
            <Form.Item className="margin-top-lg">
              <Flex gap="large">
                {isCheckStep && (
                  <CustomButton
                    htmlType="button"
                    disabled={loading}
                    loading={loading}
                    type="default"
                    onClick={handleCancel}
                    className="full-width"
                  >
                    {CANCEL_BUTTON_TEXT}
                  </CustomButton>
                )}
                <CustomButton
                  htmlType="submit"
                  disabled={loading || uploadedFiles.length === 0 || buttonDisabled}
                  loading={loading}
                  id={getIdForSubmitButton()}
                  tooltip={getSubmitButtonTooltip()}
                  fullWidth={!isCheckStep}
                  className="full-width"
                >
                  {getSubmitButtonText(isImpContract && bidFrame && !isWalletChecked && loading && isSendStep)}
                </CustomButton>
              </Flex>
            </Form.Item>
          )}
        </Form>
      </Flex>
    </>
  );
});
